import type { PosProduct, PosVariant } from './api';

/**
 * POS product search. Pure and client-side: `GET /pos/products` returns the
 * whole branch catalog unpaginated, so filtering here is instant and needs no
 * round-trip. Matching follows the Inventory tab's rule (trim → lowercase →
 * substring), extended to variations so searching "Large" surfaces the parent
 * tile rather than nothing.
 */

const norm = (s: string) => s.trim().toLowerCase();

/** Does this product match the query, by its own name/SKU or any variation's? */
export function posProductMatches(p: PosProduct, q: string): boolean {
  const f = norm(q);
  if (!f) return true;
  if (p.name.toLowerCase().includes(f) || p.sku.toLowerCase().includes(f)) return true;
  return p.variants.some(
    (v) => v.name.toLowerCase().includes(f) || v.sku.toLowerCase().includes(f),
  );
}

export function filterPosProducts(products: PosProduct[], q: string): PosProduct[] {
  const f = norm(q);
  if (!f) return products;
  return products.filter((p) => posProductMatches(p, f));
}

/**
 * Resolve a barcode scan. A scanner types the SKU then sends Enter, so this is
 * a FULL-string, case-insensitive SKU compare — never a substring.
 *
 * Returns the variation when a variation's own SKU was scanned (the scan has
 * already chosen it, so the picker should be skipped), or `{ variant: null }`
 * for a parent SKU. Returns `null` when nothing matches OR when more than one
 * product/variation claims that SKU — an ambiguous scan must never silently
 * add the wrong item.
 */
export function exactSkuMatch(
  products: PosProduct[],
  q: string,
): { product: PosProduct; variant: PosVariant | null } | null {
  const f = norm(q);
  if (!f) return null;

  const hits: Array<{ product: PosProduct; variant: PosVariant | null }> = [];
  for (const p of products) {
    if (p.sku.toLowerCase() === f) hits.push({ product: p, variant: null });
    for (const v of p.variants) {
      if (v.sku.toLowerCase() === f) hits.push({ product: p, variant: v });
    }
  }
  return hits.length === 1 ? hits[0]! : null;
}
